// eslint-disable-next-line import/no-unresolved
import brand from '@brand/brand.js';
import { zodResolver } from '@hookform/resolvers/zod';
import ClockIcon from 'jsx:../../../assets/clock-icon.svg';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as z from 'zod';
import { IS_PRODUCTION } from '../../../../shared/constants';
import { useInterval } from '../../../../shared/hooks/use-interval';
import { useRequestOtp } from '../../api/use-request-otp';
import { useValidateOtp } from '../../api/use-validate-otp';
import { BrandedLayout } from '../../components/layouts/branded-layout';
import { SubmitButton } from '../../components/submit-button';
import { Button } from '../../components/ui/button';
import { ErrorBlock } from '../../components/ui/error-block';
import { InfoBlock } from '../../components/ui/info-block';
import { Input } from '../../components/ui/input';
import { useCodeStore } from '../../store/use-code-store';
import { FINAL_INVITE_STATUSES, useInviteStore } from '../../store/use-invite-store';
import { cleanPhone } from '../../utils/phone';
import { formatTime } from '../../utils/time';

const resolver = zodResolver(
  z.object({
    code: z.string().regex(/^\d{4}$/),
  }),
);

function useCodeExpiry() {
  const { expiresAt } = useCodeStore();
  const remaining = useInterval(
    () => Math.max(Math.floor((expiresAt - Date.now()) / 1000), 0),
    1000,
  );
  return { remaining, state: remaining <= 0 ? 'expired' : remaining <= 10 ? 'expiring' : 'valid' };
}

let defaultValues = { code: '' };
if (!IS_PRODUCTION) {
  defaultValues.code = localStorage.getItem('soopa-invite-code-otp') ?? '';
}

export function Code() {
  const { state } = useCodeExpiry();
  return state === 'expired' ? <CodeExpired /> : <CodeValid />;
}

function CodeValid() {
  const { t } = useTranslation('page.code');
  const navigate = useNavigate();
  const { invite } = useInviteStore();
  const { validityMinutes } = useCodeStore();
  const validateOtp = useValidateOtp();
  const form = useForm({
    defaultValues,
    resolver,
    mode: 'onChange',
  });

  const onSubmit = useCallback(
    async ({ code }) => {
      try {
        const { status } = await validateOtp.execute(code);
        // Only need to continue when the invite status is not final. If it is
        // final the EnsureInvite component will display the correct page.
        if (!FINAL_INVITE_STATUSES.includes(status?.toLowerCase())) {
          navigate(`/${invite.reference}/continue`);
        }
      } catch {
        // Handled
      }
    },
    [invite.reference, navigate, validateOtp],
  );

  return (
    <BrandedLayout>
      <div className="mb-5">
        <h1 className="title">{t('title')}</h1>
        <Trans
          i18nKey="text"
          ns="page.code"
          components={{
            p: <p />,
            strong: <strong className="font-semibold" />,
            PhonePlaceholder: invite?.phone,
          }}
          values={{ validityMinutes }}
        />
      </div>

      <ErrorBlock error={validateOtp.error} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="pt-2.5">
          <Input name="code" autoFocus autoComplete="one-time-code" isRequired />
          <div className="my-3.5 flex items-center gap-2.5">
            <ClockIcon className="text-primary-active" aria-hidden="true" />
            <span className="text-sm leading-none text-light-500">
              <TimeRemaining />
            </span>
          </div>
          <SubmitButton />
        </form>

        <InfoBlock>
          <h3 className="subtitle">{t('noCode')}</h3>
          <ResendButton />
        </InfoBlock>
      </FormProvider>

      <InfoBlock>
        <Trans
          i18nKey="helpMessage"
          ns="page.code"
          values={brand}
          components={{
            p: <p />,
            br: <br />,
            strong: <strong className="font-semibold" />,
            a: <a href={`tel:${cleanPhone(brand.phone)}`} />,
          }}
        />
      </InfoBlock>
    </BrandedLayout>
  );
}

function CodeExpired() {
  const { t } = useTranslation('page.code');
  return (
    <BrandedLayout>
      <div className="mb-5">
        <h1 className="title">{t('titleExpired')}</h1>
        <Trans
          i18nKey="textExpired"
          t={t}
          components={{
            p: <p />,
            strong: <strong className="font-semibold" />,
          }}
        />
        <div className="mt-5">
          <ResendButton variant="primary" />
        </div>
        <InfoBlock>
          <Trans
            i18nKey="helpMessage"
            ns="page.code"
            values={brand}
            components={{
              p: <p />,
              br: <br />,
              strong: <strong className="font-semibold" />,
              a: <a href={`tel:${cleanPhone(brand.phone)}`} />,
            }}
          />
        </InfoBlock>
      </div>
    </BrandedLayout>
  );
}

function TimeRemaining() {
  const { t } = useTranslation('page.code');
  const { remaining, state } = useCodeExpiry();
  return (
    <span
      className="data-[state=expiring]:text-warning-500 data-[state=expired]:text-error-500"
      data-state={state}
    >
      {t('remainingTime', { time: formatTime(remaining) })}
    </span>
  );
}

function ResendButton({ variant = 'outlined' }) {
  const { t } = useTranslation('page.code');
  const requestOtp = useRequestOtp();
  // TODO: handle request otp error

  const onClick = useCallback(async () => {
    await requestOtp.execute();
  }, [requestOtp]);

  return (
    <Button type="button" variant={variant} onClick={onClick}>
      {t('sendCode')}
    </Button>
  );
}
